
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';
import { mockActivities, mockDashboardData } from '../../mocks/activities';

export default function Analytics() {
  const formatCurrency = (amount: number) => {
    return `¥${(amount / 10000).toFixed(1)}万`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const completedActivities = mockActivities.filter(a => a.status === 'completed');
  const activeActivities = mockActivities.filter(a => a.status === 'active');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">数据分析</h1>
            <p className="text-gray-600">活动效果复盘与数据洞察</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline">
              <i className="ri-calendar-line mr-2"></i>
              选择时间范围
            </Button>
            <Button>
              <i className="ri-download-line mr-2"></i>
              导出报告
            </Button>
          </div>
        </div>

        {/* 核心指标概览 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {formatCurrency(mockDashboardData.totalRevenue)}
            </h3>
            <p className="text-gray-600 text-sm">总营收</p>
            <div className="flex items-center justify-center mt-2 text-green-600 text-sm">
              <i className="ri-arrow-up-line mr-1"></i>
              <span>+12.5%</span>
            </div>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-add-line text-blue-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {formatNumber(mockDashboardData.newMembers)}
            </h3>
            <p className="text-gray-600 text-sm">新增会员</p>
            <div className="flex items-center justify-center mt-2 text-green-600 text-sm">
              <i className="ri-arrow-up-line mr-1"></i>
              <span>+8.3%</span>
            </div>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-exchange-line text-purple-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {mockDashboardData.avgConversionRate}%
            </h3>
            <p className="text-gray-600 text-sm">平均转化率</p>
            <div className="flex items-center justify-center mt-2 text-green-600 text-sm">
              <i className="ri-arrow-up-line mr-1"></i>
              <span>+2.1%</span>
            </div>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-wallet-line text-orange-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {((mockDashboardData.totalSpent / mockDashboardData.totalBudget) * 100).toFixed(1)}%
            </h3>
            <p className="text-gray-600 text-sm">预算使用率</p>
            <div className="flex items-center justify-center mt-2 text-yellow-600 text-sm">
              <i className="ri-arrow-up-line mr-1"></i>
              <span>+5.2%</span>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 活动效果对比 */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">活动效果对比</h2>
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm">
                  <i className="ri-bar-chart-line mr-1"></i>
                  柱状图
                </Button>
                <Button variant="ghost" size="sm">
                  <i className="ri-line-chart-line mr-1"></i>
                  折线图
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              {completedActivities.map((activity) => {
                const memberCompletionRate = (activity.currentKPI.newMembers / activity.targetKPI.newMembers) * 100;
                const revenueCompletionRate = (activity.currentKPI.revenue / activity.targetKPI.revenue) * 100;
                
                return (
                  <div key={activity.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900">{activity.name}</h3>
                      <Badge variant={revenueCompletionRate >= 100 ? 'success' : revenueCompletionRate >= 80 ? 'warning' : 'error'}>
                        {revenueCompletionRate >= 100 ? '超额完成' : revenueCompletionRate >= 80 ? '基本达标' : '未达标'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-1">新增会员</p>
                        <p className="font-semibold text-gray-900">
                          {activity.currentKPI.newMembers.toLocaleString()}
                        </p>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className={`h-1.5 rounded-full ${memberCompletionRate >= 100 ? 'bg-green-500' : memberCompletionRate >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(memberCompletionRate, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{memberCompletionRate.toFixed(1)}% 完成</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600 mb-1">转化率</p>
                        <p className="font-semibold text-gray-900">
                          {activity.currentKPI.conversionRate}%
                        </p>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className={`h-1.5 rounded-full ${activity.currentKPI.conversionRate >= activity.targetKPI.conversionRate ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${(activity.currentKPI.conversionRate / activity.targetKPI.conversionRate) * 100}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">目标 {activity.targetKPI.conversionRate}%</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600 mb-1">营收</p>
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(activity.currentKPI.revenue)}
                        </p>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className={`h-1.5 rounded-full ${revenueCompletionRate >= 100 ? 'bg-green-500' : revenueCompletionRate >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(revenueCompletionRate, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{revenueCompletionRate.toFixed(1)}% 完成</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* 实时活动监控 */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">实时活动监控</h2>
              <Button variant="ghost" size="sm">
                <i className="ri-refresh-line mr-1"></i>
                刷新
              </Button>
            </div>
            <div className="space-y-4">
              {activeActivities.map((activity) => (
                <div key={activity.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">{activity.name}</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-green-600">进行中</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {activity.currentKPI.newMembers.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">当前新增会员</p>
                      <p className="text-xs text-green-600">
                        +{Math.floor(Math.random() * 50 + 10)} (过去1小时)
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(activity.currentKPI.revenue)}
                      </p>
                      <p className="text-sm text-gray-600">当前营收</p>
                      <p className="text-xs text-green-600">
                        +{formatCurrency(Math.floor(Math.random() * 50000 + 10000))} (过去1小时)
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">整体进度</span>
                    <span className="font-medium">{activity.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${activity.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* 优化建议 */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">优化建议</h2>
            <Button variant="outline" size="sm">
              <i className="ri-robot-line mr-2"></i>
              AI 生成建议
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                title: '提升转化率',
                description: '双十一活动转化率低于预期，建议优化落地页设计和券权益设置',
                priority: 'high',
                impact: '预计提升 15%'
              },
              {
                title: '预算优化',
                description: '会员活动成本效益较高，建议增加此类活动预算分配',
                priority: 'medium',
                impact: '预计节省 20%'
              },
              {
                title: '渠道调整',
                description: '社交媒体渠道表现突出，建议扩大投放比例',
                priority: 'medium',
                impact: '预计增长 25%'
              },
              {
                title: '时间优化',
                description: '周末活动效果显著优于工作日，建议调整活动时间安排',
                priority: 'low',
                impact: '预计提升 10%'
              },
              {
                title: '目标人群',
                description: '25-35岁用户群体响应率最高，建议精准投放',
                priority: 'high',
                impact: '预计提升 30%'
              },
              {
                title: '内容优化',
                description: '视频内容互动率高于图文，建议增加视频素材制作',
                priority: 'medium',
                impact: '预计提升 18%'
              }
            ].map((suggestion, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{suggestion.title}</h3>
                  <Badge 
                    variant={suggestion.priority === 'high' ? 'error' : suggestion.priority === 'medium' ? 'warning' : 'info'}
                    size="sm"
                  >
                    {suggestion.priority === 'high' ? '高' : suggestion.priority === 'medium' ? '中' : '低'}优先级
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mb-3">{suggestion.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-600">{suggestion.impact}</span>
                  <Button variant="ghost" size="sm">
                    <i className="ri-arrow-right-line"></i>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}
